import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { type FC, type ReactNode } from 'react';
import { Collapse, IconButton, Table, TableBody, TableCell, TableRow } from '@mui/material';

import { observer } from 'mobx-react';
import styled, { useTheme } from 'styled-components';
import { EyeCheckboxRender } from '@/components/common/expandableTable/EyeCheckboxRender.tsx';
import type { IndicationsBoxController } from '@/components/boxes/indicationsBoxes/indicationBoxController.ts';
import { CollapsibleTableRow } from '@/components/boxes/indicationsBoxes/CollapsibleTableRow.tsx';
import { PenCheckboxRender } from '@/components/common/expandableTable/PenCheckboxRender';

export const CollapsibleContent: FC<{
  label: string;
  descriptors: Array<string>;
  children: ReactNode;
  controller: IndicationsBoxController;
  isCollapsed: boolean;
  visibilityState: boolean | 'mixed';
  beingEditedState?: boolean | 'mixed';
  toggleIsCollapsed: () => void;
}> = observer(
  ({ label, descriptors, children, controller, isCollapsed, toggleIsCollapsed, visibilityState, beingEditedState }) => {
    const theme = useTheme();

    return (
      <>
        <CollapsibleTableRow
          sx={{ backgroundColor: theme.customPalette.panels.collapsibleBoxes.header.background }}
          contentCell={
            <GroupItem>
              <CollapseIcon aria-label="expand row" size="small" onClick={toggleIsCollapsed}>
                {!isCollapsed ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </CollapseIcon>
              {label}
            </GroupItem>
          }
          visibilityCell={
            <EyeCheckboxRender
              onChange={(checked: boolean): void => {
                controller.updateAllIndicationsVisibility(descriptors, checked);
              }}
              isChecked={visibilityState}
            />
          }
          beingEditedCell={
            beingEditedState !== undefined ? (
              <PenCheckboxRender
                onChange={(checked: boolean): void => controller.updateAllViewItemsBeingEdited(descriptors, checked)}
                isChecked={beingEditedState}
              />
            ) : undefined
          }
        />
        <TableRow>
          <TableCell colSpan={5} style={{ padding: 0 }}>
            <Collapse in={!isCollapsed}>
              <Table size="small">
                <TableBody>{children}</TableBody>
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  }
);

const CollapseIcon = styled(IconButton)`
  color: ${({ theme }): string => theme.customPalette.panels.side.font};
  margin-right: 1em;
`;

const GroupItem = styled.div`
  font-weight: bold;
`;
